import CollapsibleCard from '@/components/Elements/CollapsibleCard'
import { OfferApprovalType, OfferBookingStatus } from '@/features/offers/constants'
import { formatOffer } from '@/features/offers/helpers/formatOffer'
import useOffer from '@/features/offers/hooks/useOffer'
import useOfferPR from '@/features/offers/hooks/useOfferPR'
import ApproverSlotActionButton from '@/features/offers/offer-detail/components/ApproverSlotActionButton'
import { APPROVAL_TABLE_TYPE } from '@/features/offers/offer-detail/helper/approverTableColumn.helper'
import { selectOfferDetail } from '@/features/offers/slices/offerDetail.slice'
import { showPROnHoldConfirmationModal } from '@/features/offers/slices/offerModal.slice'
import useUser from '@/features/users/useUser'
import { authSelector } from '@/stores/auth/authSlice'
import { setApprovalInvitedUsers, showApprovalRemarkModal } from '@/stores/offers/offersSlice'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { showModal } from '@/stores/ui/modalSlice'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import { createExcelBlob } from '@/utils/fileHelper'
import { numberWithCommas, paddingZeroString } from '@/utils/stringHelper'
import { Badge, Button, Flex, Heading, Text, Spacer } from '@chakra-ui/react'
import { PRStatus } from '@eapproval/offer-shared'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { usePRAccess } from '../../PRAccessProvider'
import PRApproverTable from './PRApproverTable'
import { getPRStatusBadgeColor, normalizePRSubmissionForApi } from './PRSubmission.helper'
import PRSubmissionDetailTable from './PRSubmissionDetailTable'
import PRSubmissionGeneralInfo from './PRSubmissionGeneralInfo'
import usePRSubmissionEditRights from './usePRSubmissionEditRights'

const PRSubmission = ({ costBudgetIndex, vendorSelectionIndex, submissionIndex, removePRSubmission }) => {
  const { user } = useSelector(authSelector)
  const { adminMode } = useSelector(selectOfferDetail)
  const { canEditPRSubmission } = usePRAccess()
  const submissionPath = `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.submissions.${submissionIndex}`

  const submissionDetail = useWatch({
    name: submissionPath,
  })
  const status = useWatch({ name: 'status' })
  const offerId = useWatch({ name: '_id' })
  const costBudgetType = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.type` })

  //Display Control
  const canEditCurrentPRSubmission = canEditPRSubmission(costBudgetIndex)

  const {
    canEditPRSubmissionGeneralField,
    showPRApprovalButtons,
    showPROnHoldButtons,
    showApproveOnHoldButton,
    showPRArchiveButton,
    showPRDeleteButton,
    showPRSubmitButton,
    showPRReSubmitButton,
    canUploadSubcontractorAgreement,
    canEditSubcontractorAgreementRequired,
    approverDetail,
    inviteSlots,
    replySlots,
    canEditPRSubmissionTable,
    canEditPRSubmissionQuantity,
  } = usePRSubmissionEditRights(submissionDetail, user, canEditCurrentPRSubmission, status, adminMode)

  const dispatch = useDispatch()
  const { getValues, setValue } = useFormContext()
  const { useUpdateOffer } = useOffer()
  const { useUserOptions } = useUser()
  const { useSubmitPR, useApprovePR, useRejectPR, useResubmitPR, useArchivePR, useDeletePR } = useOfferPR()

  const submitPR = useSubmitPR()
  const approvePR = useApprovePR()
  const rejectPR = useRejectPR()
  const resubmitPR = useResubmitPR()
  const archivePR = useArchivePR()
  const deletePR = useDeletePR()
  const updateOffer = useUpdateOffer()

  const { data: allUserOptionsForInviter } = useUserOptions({
    valueKey: '_id',
    status: 'active',
  })

  const normalizedSubmissionDetail = useMemo(() => normalizePRSubmissionForApi(submissionDetail), [submissionDetail])

  const getUserDisplayName = (userDetail) => {
    if (!userDetail) {
      return '(-)'
    }

    if (typeof userDetail === 'object' && userDetail?.fullName) {
      return userDetail.fullName
    }

    const userId = userDetail?._id || userDetail
    return allUserOptionsForInviter?.find((option) => option?.value === userId)?.label || '(-)'
  }

  const PONumber = submissionDetail?.PONumber ?? '(-)'
  const requestorName = getUserDisplayName(submissionDetail?.requestor)
  const raiserName = getUserDisplayName(submissionDetail?.raiser)
  const requestorId = submissionDetail?.requestor?._id || submissionDetail?.requestor
  const raiserId = submissionDetail?.raiser?._id || submissionDetail?.raiser
  const shouldShowRaiserName = requestorId && raiserId && String(requestorId) !== String(raiserId)

  const lumpSumDiscountAmount = useMemo(() => {
    if (!Array.isArray(submissionDetail?.requestedDetails)) {
      return 0
    }

    return submissionDetail.requestedDetails.reduce((total, row) => {
      const discount = Number(row?.lumpSumDiscount ?? 0) * (row?.quantity ?? 0)
      return total + (Number.isFinite(discount) ? discount : 0)
    }, 0)
  }, [submissionDetail?.requestedDetails])

  const currency = submissionDetail?.currency ?? 'HKD'

  const isBookingStatusAllowedForPRSubmission = (offerStatus) => {
    return offerStatus === OfferBookingStatus.BOOKING_SUBMITTED || offerStatus === OfferBookingStatus.BOOKING_COMPLETED
  }

  const handleSubmitPR = async () => {
    try {
      //Save Offer First
      const currentOffer = getValues()

      if (!isBookingStatusAllowedForPRSubmission(currentOffer?.status)) {
        toast.error('Offer must be in Booking Submitted or Booking Completed status before submitting PR')
        return
      }

      const formattedOffer = formatOffer(currentOffer)

      dispatch(showLoadingOverlay())

      await updateOffer.mutateAsync({
        id: currentOffer._id,
        data: formattedOffer,
      })
      await submitPR.mutateAsync({
        offerId: offerId,
        costBudgetIndex: costBudgetIndex,
        submissionIndex: submissionIndex,
        vendorSelectionIndex: vendorSelectionIndex,
        submissionDetail: submissionDetail,
      })
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }
  const handleResubmitPR = async () => {
    try {
      //Save Offer First
      const currentOffer = getValues()

      if (!isBookingStatusAllowedForPRSubmission(currentOffer?.status)) {
        toast.error('Offer must be in Booking Submitted or Booking Completed status before submitting PR')
        return
      }

      const formattedOffer = formatOffer(currentOffer)

      dispatch(showLoadingOverlay())

      await updateOffer.mutateAsync({
        id: currentOffer._id,
        data: formattedOffer,
      })
      await resubmitPR.mutateAsync({
        offerId: offerId,
        costBudgetIndex: costBudgetIndex,
        submissionIndex: submissionIndex,
        vendorSelectionIndex: vendorSelectionIndex,
      })
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const openApprovalRemarkModalForPR = (approvalType) => {
    dispatch(
      showApprovalRemarkModal({
        approvalType,
        approverType: approverDetail?.type,
        offerId: offerId,
        remark: '',
        updatePath: '',
        meta: {
          costBudgetIndex: costBudgetIndex,
          submissionIndex: submissionIndex,
          vendorSelectionIndex: vendorSelectionIndex,
          submissionDetail: normalizedSubmissionDetail,
        },
      })
    )
  }

  const handleApprovePR = () => {
    openApprovalRemarkModalForPR(OfferApprovalType.PR_APPROVE)
  }

  const handleRejectPR = () => {
    openApprovalRemarkModalForPR(OfferApprovalType.PR_REJECT)
  }

  const handleOnHoldPR = () => {
    openApprovalRemarkModalForPR(OfferApprovalType.PR_ON_HOLD)
  }

  const handleSelectNewQuotation = () => {
    const onHoldDate = getValues(`${submissionPath}.onHoldDate`)

    const selectedVendor = getValues(
      `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.vendor`
    )
    const selectedUploadHistoryIndex = getValues(
      `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.uploadHistoryIndex`
    )

    const allVendorDetails = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors`)
    const costBudgetDetail = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail`)

    const selectedVendorDetail = allVendorDetails.find((detail) => detail.vendor === selectedVendor)
    const vendorIndex = allVendorDetails.findIndex((detail) => detail.vendor === selectedVendor)
    const costBudgetId = costBudgetDetail?._id || costBudgetDetail

    const selectedQuotation =
      Number.isInteger(selectedUploadHistoryIndex) && selectedUploadHistoryIndex >= 0
        ? selectedVendorDetail?.history?.[selectedUploadHistoryIndex]
        : null

    const currentSelectedQuotation = selectedQuotation
      ? [
          {
            ...selectedQuotation,
            historyIndex: selectedUploadHistoryIndex,
            isOriginalQuotation: true,
          },
        ]
      : []

    const uploadedAfterOnHold = selectedVendorDetail.history
      .map((his, historyIndex) => {
        return {
          ...his,
          historyIndex: historyIndex,
        }
      })
      .filter((his) => his.actionType === 'upload')

    const newQuotations = currentSelectedQuotation.concat(
      uploadedAfterOnHold.filter((his) => his.historyIndex !== selectedUploadHistoryIndex)
    )

    dispatch(
      showPROnHoldConfirmationModal({
        newQuotations: newQuotations,
        offerId: offerId,
        costBudgetId,
        vendorIndex,
        costBudgetIndex: costBudgetIndex,
        vendorSelectionIndex: vendorSelectionIndex,
        submissionIndex: submissionIndex,
      })
    )
  }

  const handleArchivePR = async () => {
    try {
      //Save Offer First

      await archivePR.mutateAsync({
        offerId: offerId,
        costBudgetIndex: costBudgetIndex,
        submissionIndex: submissionIndex,
        vendorSelectionIndex: vendorSelectionIndex,
      })
    } catch (err) {
      console.error(err)
    }
  }
  const handleDeletePR = async () => {
    try {
      //Save Offer First
      if (submissionDetail?._id) {
        //Already saved to DB, call delete API
        await deletePR.mutateAsync({
          offerId: offerId,
          costBudgetIndex: costBudgetIndex,
          submissionIndex: submissionIndex,
          vendorSelectionIndex: vendorSelectionIndex,
        })
      } else {
        //Just remove from local form state
        removePRSubmission(submissionIndex)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const handleDownloadBuyNow = (e) => {
    try {
      e.stopPropagation()
      if (!Array.isArray(submissionDetail?.requestedDetails)) {
        return
      }

      const PRNumber = submissionDetail?.PRNumber ?? ''
      const requestorName = submissionDetail?.requestor?.fullName ?? ''

      const formattedData = submissionDetail?.requestedDetails.map((detail, detailIndex) => {
        return {
          'Line number': detailIndex + 1,
          'Material Number (SAP)': paddingZeroString(
            detail?.detail?.SAPMaterialNumber || detail?.SAPMaterialNumber || '',
            18
          ),
          'Manual Description': detail?.description ?? detail?.productDescription ?? '',
          'QTY To Order': detail?.quantity ?? '',
          'Unit Price': detail?.unitRate ?? detail?.detail?.unitRate ?? 0,
          Unit: detail?.unit ?? '',
          'Total Price': detail?.totalPrice ?? 0,
          'Discount %': detail?.discount ?? 0,
          'Lump Sum Discount': detail?.lumpSumDiscount ?? 0,
          Category: detail?.category ?? '',
          Brand: detail?.brand ?? '',
          'Model Number': detail?.modelNumber ?? '',
          'Item Text': `${PRNumber}-${detailIndex + 1}-${requestorName}`,
          'Scope Of Work': detail?.scopeOfWork ?? '',
          Country: detail?.country ?? '',
          AL: detail?.AL ?? '',
          ECCN: detail?.ECCN ?? '',
          'HS Code': detail?.HSCode ?? '',
        }
      })

      const excelBlob = createExcelBlob(formattedData)
      saveAs(excelBlob, `E-Approval PR Buy Now-${submissionDetail?.PRNumber}.xlsx`)
    } catch (err) {
      toast.error('Error in Downloading file: ' + err.message)
    }
  }

  const handleOpenPRInvitationDetailModal = (slot) => {
    const { approverIndex, detail: approverSlotDetail } = slot

    dispatch(setApprovalInvitedUsers(approverSlotDetail?.invitedUsers || []))
    dispatch(
      showModal({
        modalType: MODAL_TYPES.INVITATION_DETAIL,
        modalProps: {
          updateInvitationParameter: {
            offerId,
            costBudgetIndex,
            vendorSelectionIndex,
            submissionIndex,
            approverIndex,
          },
          approvalRole: approverSlotDetail?.type,
          canManageInvitations: inviteSlots.length > 0,
          tableType: APPROVAL_TABLE_TYPE.PR_APPROVAL,
        },
      })
    )
  }

  const handleOpenReplyInvitationModal = (slot) => {
    const { approverIndex, detail: inviterDetail } = slot

    dispatch(setApprovalInvitedUsers(inviterDetail?.invitedUsers || []))
    dispatch(
      showModal({
        modalType: MODAL_TYPES.INVITATION_DETAIL,
        modalProps: {
          updateInvitationParameter: {
            offerId,
            costBudgetIndex,
            vendorSelectionIndex,
            submissionIndex,
            approverIndex,
          },
          approvalRole: inviterDetail?.type,
          canManageInvitations: inviteSlots.length > 0,
          tableType: APPROVAL_TABLE_TYPE.PR_APPROVAL,
        },
      })
    )
  }

  const handleOpenECCNDetailModel = (ECCNDetail = []) => {
    dispatch(
      showModal({
        modalType: MODAL_TYPES.ECCN_DETAIL,
        modalProps: {
          ECCNDetail,
          disabled: true,
        },
      })
    )
  }

  return (
    <CollapsibleCard
      hasBorder={true}
      defaultIsOpen={[PRStatus.DRAFT, PRStatus.REJECTED, PRStatus.PARKED, PRStatus.SUBMITTED].includes(
        submissionDetail.status
      )}
      headerElement={
        <Flex direction="column" w="100%">
          <Flex gap="1" justifyContent={'space-between'}>
            <Heading size="md">{`PR:  ${submissionDetail?.PRNumber ?? ''}`}</Heading>
            <Flex>
              <Text as="b">PO Number: </Text>
              {PONumber}
            </Flex>
            <Flex direction="row" gap="1" fontSize={'18'}>
              {lumpSumDiscountAmount > 0 && (
                <>
                  <Text as="b">Lump Sum Discount ({currency}): </Text>
                  <Text>{`$${numberWithCommas(lumpSumDiscountAmount)}`}</Text>
                </>
              )}
              <Text as="b">Net Amount ({currency}):</Text>
              <Text>${numberWithCommas(submissionDetail?.totalAmount ?? 0)}</Text>
            </Flex>
          </Flex>
          <Flex gap="1" alignItems={'center'}>
            <Badge colorScheme={getPRStatusBadgeColor(submissionDetail?.status)}>{submissionDetail?.status}</Badge>

            <Flex alignItems={'center'} gap="2">
              <Text>
                <b>Request by:</b> {requestorName}
                {shouldShowRaiserName ? `(${raiserName})` : ''}
              </Text>
            </Flex>
            <Flex alignItems={'center'} gap="2">
              <Text>
                <b>Request Date:</b>
                {submissionDetail?.requestDate ? moment(submissionDetail.requestDate).format('YYYY-MM-DD') : '(-)'}
              </Text>
            </Flex>
            <Spacer />
            <Flex gap="2">
              {showPRArchiveButton && (
                <Button colorScheme="red" onClick={handleArchivePR}>
                  Archive
                </Button>
              )}
              {showPRDeleteButton && (
                <>
                  <Button colorScheme="red" onClick={handleDeletePR}>
                    Delete
                  </Button>
                </>
              )}
              <Button colorScheme="teal" onClick={handleDownloadBuyNow}>
                Download Buy Now
              </Button>
            </Flex>
          </Flex>
        </Flex>
      }
    >
      <PRSubmissionDetailTable
        submissionPath={submissionPath}
        costBudgetType={costBudgetType}
        canEditPRSubmissionTable={canEditPRSubmissionTable}
        canEditPRSubmissionQuantity={canEditPRSubmissionQuantity}
        handleOpenECCNDetailModel={handleOpenECCNDetailModel}
      />

      <PRSubmissionGeneralInfo
        costBudgetIndex={costBudgetIndex}
        vendorSelectionIndex={vendorSelectionIndex}
        submissionIndex={submissionIndex}
        canEditPRSubmissionGeneralField={canEditPRSubmissionGeneralField}
        canEditSubcontractorAgreementRequired={canEditSubcontractorAgreementRequired}
        canUploadSubcontractorAgreement={canUploadSubcontractorAgreement}
        submissionDetail={submissionDetail}
      />

      <PRApproverTable
        costBudgetIndex={costBudgetIndex}
        submissionIndex={submissionIndex}
        vendorSelectionIndex={vendorSelectionIndex}
        canEditPR={canEditPRSubmissionGeneralField}
      />

      <Flex my="2" gap="2">
        {showPRSubmitButton && (
          <Button colorScheme="blue" onClick={handleSubmitPR}>
            Submit PR
          </Button>
        )}

        {showPRReSubmitButton && (
          <Button colorScheme="blue" onClick={handleResubmitPR}>
            Resubmit PR
          </Button>
        )}

        {showApproveOnHoldButton && (
          <>
            <Button colorScheme="blue" onClick={handleSelectNewQuotation}>
              Select New Quotation
            </Button>
            <Button colorScheme="green" onClick={handleApprovePR}>
              Approve
            </Button>
            <Button colorScheme="red" onClick={handleRejectPR}>
              Reject
            </Button>
            <ApproverSlotActionButton
              label="Invite"
              colorScheme="teal"
              slots={inviteSlots}
              onSelect={handleOpenPRInvitationDetailModal}
            />
          </>
        )}

        {showPRApprovalButtons && (
          <>
            <Button colorScheme="green" onClick={handleApprovePR}>
              Approve
            </Button>
            <Button colorScheme="red" onClick={handleRejectPR}>
              Reject
            </Button>
            <ApproverSlotActionButton
              label="Invite"
              colorScheme="teal"
              slots={inviteSlots}
              onSelect={handleOpenPRInvitationDetailModal}
            />
          </>
        )}

        {showPROnHoldButtons && (
          <>
            <Button colorScheme="blue" onClick={handleOnHoldPR}>
              On hold
            </Button>
          </>
        )}

        <ApproverSlotActionButton
          label="Reply"
          colorScheme="blue"
          slots={replySlots}
          onSelect={handleOpenReplyInvitationModal}
        />
      </Flex>
    </CollapsibleCard>
  )
}

export default PRSubmission
